import { PHONE_INCOMING_128, PHONE_INCOMING_128_S3 } from 'Constants/assets';
import {
  IS_ELECTRON,
  NODE_ENV_PRODUCTION,
  NODE_ENV_TEST,
  SIP_USER_AGENT,
} from 'Constants/env';
import {
  SELECTED_MICROPHONE,
  SELECTED_RING_DEVICE,
  SELECTED_SPEAKER,
  SHOW_INCOMING_CALL,
} from 'Constants/localstorage';
import { SETTINGS_SUBPATHS } from 'Constants/routing';
import { WN_INCOMING_CALL_PREFIX } from 'Constants/webNotifications';
import {
  AxiosResponseT,
  ResultsCollectionObservable,
} from 'Interfaces/axiosResponse';
import { IMicrophoneOption } from 'Interfaces/phoneStore';
import localforage from 'localforage';
import { isEmpty } from 'lodash';
import {
  action,
  computed,
  IObservableArray,
  observable,
  runInAction,
  when,
  makeObservable,
  reaction,
} from 'mobx';
import { IPromiseBasedObservable, createTransformer } from 'mobx-utils';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { fetchContactsWithPhoneNumber } from 'Modules/contacts/requests';
import { useNotificationStore } from 'Modules/notification/store';
import { usePersonStore } from 'Modules/person/index.store';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import moment from 'moment-timezone';
import {
  Inviter,
  Registerer,
  RegistererState,
  Session,
  Transport,
  UserAgent,
  UserAgentOptions,
  URI,
} from 'sip.js';
import { BaseStore } from 'Stores/BaseStore';
import { RootStore } from 'Stores/RootStore';
import { isNullOrUndefined } from 'util';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { sendIpcLog } from 'Utils/ipcRendererEvents';
import { getLogger, Logger } from 'Utils/logger';
import { bugsnagClient } from 'Utils/logUtils';
import { logError } from 'Utils/phoneLogUtils';
import PhoneNumberUtil, {
  formatNumberWithNationalCode,
  isPhoneNumber,
} from 'Utils/phoneUtil';
import { searchPersonBySip } from 'Utils/searchPersonBySip';
import { stopMediaStream } from 'Utils/streamUtils';
import { v4 as uuid } from 'uuid';
import { MessageModel, PhoneCallModel } from '../models';

const TIME_CALL_ALERT_INTERVAL_MS = 10000;
const TIME_SECOND_MS = 1000;
const TIMEOUT_SECONDS = 13;
const CONNECTION_LOSS_FEEDBACK_DELAY = 5000;

/**
 * SIP/Transport timeouts
 *
 * Connection timeout is set to 12 seconds, which is 1 second less than
 * the final timeout (13 seconds) to allow for final connection attempt
 */
const TRANSPORT_TIMEOUT = {
  CONNECTION: TIMEOUT_SECONDS - 1,
  RECONNECTION: TIMEOUT_SECONDS - 1,
  MAX_ATTEMPTS: 6,
};

// WebRTC connection retry
const CONNECTION_RETRY = {
  TIMER_INITIAL: 5,
  COUNTER_INITIAL: 0,
  TIMEOUT_MS: TIMEOUT_SECONDS * TIME_SECOND_MS,
};

interface IHTMLAudioElement extends HTMLAudioElement {
  setSinkId?: (sinkId: number | string) => Promise<any>;
}

export class PhoneStore extends BaseStore {
  private logger: Logger;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this);
    this.logger = getLogger();

    /**
     * Softphone Coordination Legacy/New
     * Use coordinator factory to determine if legacy softphone should initialize.
     * Coordinator handles preference checking and activation coordination.
     *
     * Note: We read directly from localStorage (sync) instead of using the Zustand store
     * because the store hydration is async and may not be complete at this point.
     *
     * We wait for the user to be logged in before initializing.
     * Using a reaction instead of waitUntilLoggedIn to avoid timeout issues during slow logins (e.g., in Electron).
     */
    const coordinator = SoftphoneCoordinator.getInstance();
    const disposer = reaction(
      () => this.rootStore.personStore.IsLoggedIn,
      (isLoggedIn) => {
        if (isLoggedIn) {
          const personId = this.rootStore.personStore.loggedInPersonId;
          const isNewSoftphoneEnabled =
            coordinator.readPreferenceSync(personId);

          if (coordinator.shouldInitializeLegacy(isNewSoftphoneEnabled)) {
            console.log('[softphone:phonestore] Initializing legacy softphone SIP');
            this.runRegister();
            this.initializeNetworkListener();
          }
          disposer();
        }
      },
      { fireImmediately: true }
    );

    reaction(
      () => this.isWebRTCReconnecting,
      (isReconnecting) => {
        if (isReconnecting) {
          console.log('isReconnecting', isReconnecting);
        }
      }
    );
  }

  private networkListenerInitialized = false;

  @action
  initializeNetworkListener() {
    if (this.networkListenerInitialized || typeof window === 'undefined')
      return;

    this.networkListenerInitialized = true;

    window.addEventListener('offline', () => this.handleNetworkLoss());
    window.addEventListener('online', () => this.handleNetworkRestored());
  }

  @action
  handleNetworkLoss() {
    if (this.IsActiveCall) {
      this.noInternetOnActiveCall();
    }
  }

  @action
  async handleNetworkRestored() {
    if (this.IsActiveCall && this.isWebRTCReconnecting) {
      await this.attemptReconnection();
    }
  }

  public ringtonePlayPromise: Promise<void> = null;

  @observable
  public outBoundCallPutStatus: IPromiseBasedObservable<
    AxiosResponseT<ResultsCollectionObservable<MessageModel>>
  > = null;

  @observable
  public putOutboundCall: IPromiseBasedObservable<
    AxiosResponseT<ResultsCollectionObservable<MessageModel>>
  > = null;

  @observable
  public inBoundCallPutStatus: IPromiseBasedObservable<
    AxiosResponseT<ResultsCollectionObservable<MessageModel>>
  > = null;

  @observable
  public putInboundCall: IPromiseBasedObservable<
    AxiosResponseT<ResultsCollectionObservable<MessageModel>>
  > = null;

  @observable
  public localSipSession: Session;

  @observable
  public localRemoteAudio: IHTMLAudioElement;

  @action
  public setLocalRemoteAudio = (value: IHTMLAudioElement) =>
    (this.localRemoteAudio = value);

  @observable
  public localRingTone: IHTMLAudioElement;

  @action public setLocalRingTone = (value: IHTMLAudioElement) =>
    (this.localRingTone = value);

  @observable
  public localCallDisconnectedTone: IHTMLAudioElement;

  @action public setLocalCallDisconnectedTone = (value: IHTMLAudioElement) =>
    (this.localCallDisconnectedTone = value);

  @observable
  public lostConnectionTone: IHTMLAudioElement;

  @action public setLocalLostConnectionTone = (value: IHTMLAudioElement) =>
    (this.lostConnectionTone = value);

  @observable
  public localIncomingTone: IHTMLAudioElement;

  @action public setLocalIncomingTone = (value) =>
    (this.localIncomingTone = value);

  @observable
  isConnectionLossFeedbackActive = false;

  @action
  setIsConnectionLossFeedbackActive = (isActive: boolean) => {
    this.isConnectionLossFeedbackActive = isActive;
  };

  @observable
  connectionLossFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  @action
  setConnectionLossFeedbackTimer = (
    timer: ReturnType<typeof setTimeout> | null
  ) => {
    this.connectionLossFeedbackTimer = timer;
  };

  @observable
  public outBound = false;

  @observable
  public secondLocalIncomingtone: IHTMLAudioElement;

  @action public setSecondLocalIncomingtone = (value: IHTMLAudioElement) =>
    (this.secondLocalIncomingtone = value);

  @observable
  public isWebRTCReconnecting = false;

  @action
  public setIsWebRTCReconnecting = (isReconnecting: boolean) =>
    (this.isWebRTCReconnecting = isReconnecting);

  public webRTCTimeOut;

  @observable
  public isWebRTCConnected = false;

  @action
  public setIsWebRTCConnected = (isWebRTCConnected: boolean) =>
    (this.isWebRTCConnected = isWebRTCConnected);

  @observable
  public webRTCRegistrationConcluded = false;

  @action
  public setWebRTCRegistrationConcluded = (
    didWebRTCRegistrationConcluded: boolean
  ) => (this.webRTCRegistrationConcluded = didWebRTCRegistrationConcluded);

  @observable
  public webRTCTimer = 6;

  @action
  public setWebRTCTimer = (webRTCTimer: number) =>
    (this.webRTCTimer = webRTCTimer);

  @observable
  public webrtcCounter = 0;

  @action
  public setWebrtcCounter = (counter: number) => (this.webrtcCounter = counter);

  @observable
  speakerId: string;

  @observable
  ringtoneSpeakerId: string;

  @observable
  microphoneId: string;

  @observable
  theDefaultMic = 'default';

  @action
  setTheDefaultMic = (theDefaultMic: string) => {
    this.theDefaultMic = theDefaultMic;
  };

  @observable
  theDefaultSpeaker = 'default';

  @action
  setTheDefaultSpeaker = (theDefaultSpeaker: string) => {
    this.theDefaultSpeaker = theDefaultSpeaker;
  };

  @observable
  theDefaultRingDevice = 'default';

  @action
  setTheDefaultRingDevice = (theDefaultSpeaker: string) => {
    this.theDefaultRingDevice = theDefaultSpeaker;
  };

  @observable
  microphoneOptions = observable.array<IMicrophoneOption>();

  @action
  pushMicrophoneOptions = (microphoneOptions: IMicrophoneOption) => {
    this.microphoneOptions.push(microphoneOptions);
  };

  @observable
  speakerOptions = observable.array<IMicrophoneOption>();

  @action
  pushSpeakerOptions = (speakerOptions: IMicrophoneOption) => {
    this.speakerOptions.push(speakerOptions);
  };

  listOfMicroPhones = observable.map<string, boolean>();

  listOfSpeakers = observable.map<string, boolean>();

  isTheNewMicDeviceInList = createTransformer(
    (microphoneId: string) => !this.listOfMicroPhones.has(microphoneId)
  );

  isTheNewSpeakerDeviceInList = createTransformer(
    (speakers: string) => !this.listOfSpeakers.has(speakers)
  );

  @action
  checkForIODevices = () => {
    let keyCounter = 0;
    if (this.browserMicrophonePermissionState !== 'denied') {
      //@ts-ignore
      if (!window.localStream3?.active) {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(
          (resp) => {
            //@ts-ignore
            window.localStream3 = resp;
            navigator.mediaDevices.enumerateDevices().then(
              (devices) => {
                runInAction(() => {
                  this.microphoneOptions.clear();
                  this.speakerOptions.clear();
                });
                for (const device of devices) {
                  keyCounter++;
                  if (device.kind === 'audioinput') {
                    this.pushMicrophoneOptions({
                      key: keyCounter,
                      text: device.label,
                      value: device.deviceId,
                    });
                    localforage.getItem<string>(SELECTED_MICROPHONE).then(
                      (mc) => {
                        if (
                          !isNullOrUndefined(mc) &&
                          devices.find((device) => device.deviceId === mc)
                        ) {
                          this.setTheDefaultMic(mc);
                          this.configureMicrophone(mc);
                        } else {
                          if (this.isTheNewMicDeviceInList(device.deviceId)) {
                            this.setTheDefaultMic(device.deviceId);
                            this.configureMicrophone(device.deviceId);
                            return;
                          }
                          this.setTheDefaultMic('default');
                          this.configureMicrophone('default');
                        }
                      },
                      (err) =>
                        logError(
                          err,
                          err.message,
                          'PhoneStore',
                          'Selecting microphone from localForage'
                        )
                    );
                  } else if (device.kind === 'audiooutput') {
                    this.pushSpeakerOptions({
                      key: keyCounter,
                      text: device.label,
                      value: device.deviceId,
                    });
                    localforage.getItem<string>(SELECTED_SPEAKER).then(
                      (speaker) => {
                        if (
                          !isNullOrUndefined(speaker) &&
                          devices.find((device) => device.deviceId === speaker)
                        ) {
                          this.setTheDefaultSpeaker(speaker);
                          this.configureSpeaker(speaker);
                        } else {
                          if (
                            this.isTheNewSpeakerDeviceInList(device.deviceId)
                          ) {
                            this.setTheDefaultSpeaker(device.deviceId);
                            this.configureMicrophone(device.deviceId);
                            return;
                          }
                          this.setTheDefaultSpeaker('default');
                          this.configureSpeaker('default');
                        }
                      },
                      (err) =>
                        logError(
                          err,
                          err.message,
                          'PhoneStore',
                          'Selecting speaker device from localForage'
                        )
                    );
                    localforage.getItem<string>(SELECTED_RING_DEVICE).then(
                      (speaker) => {
                        if (
                          !isNullOrUndefined(speaker) &&
                          devices.find((device) => device.deviceId === speaker)
                        ) {
                          this.setTheDefaultRingDevice(speaker);
                          this.configureRingTone(speaker);
                        } else {
                          this.setTheDefaultRingDevice('default');
                          this.configureRingTone('default');
                        }
                      },
                      (err) =>
                        logError(
                          err,
                          err.message,
                          'PhoneStore',
                          'Selecting ring device from localForage'
                        )
                    );
                  }
                }
                this.setIsThereDeviceChange(false);
              },
              (err) =>
                logError(err, err.message, 'PhoneStore', 'enumerateDevices')
            );
            //@ts-ignore
            stopMediaStream(window.localStream3);
          },
          (err) => logError(err, err.message, 'PhoneStore', 'getUserMedia')
        );
      }
    }
  };

  /**
   * For Any information regarding WEBRTC follow https://webrtc.github.io/samples/
   */
  @action
  configureMicrophone = (microphoneId?: string) => {
    this.microphoneId = microphoneId;
    this.listOfMicroPhones.set(microphoneId, true);
    this.phoneCalls.forEach(this.setUserMediaInputTracks);
  };

  private setUserMediaInputTracks = (phoneCall: PhoneCallModel) => {
    //@ts-ignore
    const pc = phoneCall.session.sessionDescriptionHandler?.peerConnection;
    //@ts-ignore
    const stream: MediaStream = window.localStream;
    if (stream) {
      stream.getAudioTracks().forEach((tracks) => {
        if (
          tracks.id === this.microphoneId ||
          tracks.label.toLowerCase().includes(this.microphoneId)
        ) {
          const sender = pc.getSenders()[0];
          const peerConnectionTrack = sender?.track;

          //replace in case senderTrack is different than required one
          if (
            peerConnectionTrack &&
            !peerConnectionTrack?.label
              .toLowerCase()
              .includes(this.microphoneId) &&
            peerConnectionTrack?.id !== this.microphoneId
          ) {
            sender?.replaceTrack(tracks);
          }
        }
      });
    }
  };

  /**
   * Regarding setSinkId https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/setSinkId
   */

  @action
  setTheMic = (e: any, option: IMicrophoneOption) => {
    const { phoneStore } = this.rootStore;
    phoneStore.configureMicrophone(option.value);
    phoneStore.setTheDefaultMic(option.value);
    void localforage.setItem<string>(SELECTED_MICROPHONE, option.value);
    pushToGTMDataLayer('setMicrophone');
  };

  @action
  setTheSpeaker = (e: any, option: IMicrophoneOption) => {
    const { phoneStore } = this.rootStore;
    phoneStore.configureSpeaker(option.value);
    phoneStore.setTheDefaultSpeaker(option.value);
    void localforage.setItem<string>(SELECTED_SPEAKER, option.value);
    pushToGTMDataLayer('setSpeaker');
  };

  @action
  checkSetDevice = (typeOfDevice) => {
    const { phoneStore } = this.rootStore;
    void localforage.getItem<string>(typeOfDevice).then((device) => {
      if (isNullOrUndefined(device)) {
        phoneStore.setIsThereDeviceChange(false);
        phoneStore.checkForIODevices();
      }
    });
  };

  @action
  setRingSpeaker = (e: any, option: IMicrophoneOption) => {
    const { phoneStore } = this.rootStore;
    phoneStore.configureRingTone(option.value);
    phoneStore.setTheDefaultRingDevice(option.value);
    void localforage.setItem<string>(SELECTED_RING_DEVICE, option.value);
    pushToGTMDataLayer('setRingtoneDevice');
  };

  @action
  configureSpeaker = (speakerId?: string) => {
    this.speakerId = speakerId;
    if (!isNullOrUndefined(speakerId)) {
      this.listOfSpeakers.set(speakerId, true);
    }
    if (!isNullOrUndefined(speakerId)) {
      const noCalls = this.phoneCalls?.length === 0;
      if (noCalls && this.localRemoteAudio?.srcObject) {
        this.localRemoteAudio.srcObject = null;
      }

      // @ts-ignore
      this.localRemoteAudio
        ?.setSinkId?.(speakerId)
        .then(() => {
          console.debug(
            `Success, audio output device attached: ${speakerId} to element with ${this.localRemoteAudio.title} as source.`
          );
        })
        .catch((error) => {
          console.error(
            `Failed, Sound Notification output device attached to element with ${this.localRemoteAudio?.title} as source.`,
            error
          );

          // Send logs for issues with the audio device
          this.logger.error(
            `Audio device error: ${error?.name || 'Unknown'}; Message: ${
              error?.message || 'No error message'
            }; Device: ${speakerId}; Timestamp: ${new Date().toISOString()}`
          );

          bugsnagClient.notify(error, (event) => {
            event.severity = 'error';
            event.context = 'configureSpeaker';
          });

          useNotificationStore.getState().addNotify({
            title: 'Issue detected with your audio devices',
            message:
              'Please check your audio settings and ensure your selected devices are available.',
            type: 'error',
            autoDismiss: true,
            dismissible: 'button',
            action: {
              label: 'Audio Settings',
              onClick: () => {
                this.rootStore.routerStore.push(SETTINGS_SUBPATHS.AUDIO_VIDEO);
              },
            },
          });
        });
    }
  };

  addDeviceToAudio = (audioElem: IHTMLAudioElement, speakerId: string) => {
    try {
      // @ts-ignore
      void (
        audioElem &&
        audioElem.setSinkId(speakerId).then(() => {
          console.debug(
            `Success, OutGoing RingTone output device attached: ${speakerId} to element with ${audioElem.title} as source.`
          );
        })
      );
    } catch (error) {
      console.debug(
        `Failed, Sound Notification output device attached to element with ${audioElem?.title} as source.`
      );

      // Send logs for issues with the audio device
      this.logger.error(
        `Audio device attachment error: ${error?.name || 'Unknown'}; Message: ${
          error?.message || 'No error message'
        }; Device: ${speakerId}; Timestamp: ${new Date().toISOString()}`
      );
    }
  };

  @action
  configureRingTone = (speakerId: string) => {
    this.ringtoneSpeakerId = speakerId;
    if (!isNullOrUndefined(speakerId)) {
      this.listOfSpeakers.set(speakerId, true);
    }

    if (!isNullOrUndefined(speakerId)) {
      this.addDeviceToAudio(this.localRingTone, speakerId);
      this.addDeviceToAudio(this.localIncomingTone, speakerId);
      this.addDeviceToAudio(this.secondLocalIncomingtone, speakerId);
    }
  };

  /**
   * Keeping the new Answered call unHold and have all other calls onHold
   */
  @action
  public setLocalSession = (localSipSession: Session) => {
    this.localSipSession = localSipSession;
  };

  @observable
  browserMicrophonePermissionState: null | 'granted' | 'denied' = null;

  @action
  setBrowserMicrophonePermissionState = (state: 'granted' | 'denied') => {
    this.browserMicrophonePermissionState = state;
  };

  public conferenceRoomsIdAndNumbers = observable.map<string, string[]>();

  @observable
  public existingConferenceId?: string;

  @action
  public setExistingConferenceIdId = (existingConferenceId: string) =>
    (this.existingConferenceId = existingConferenceId);

  // controls mic enable setting on settings panel, not actual permission, though this will cause a permission to be asked for, nor microphone/phonemute
  @observable
  public testMicrophoneEnabled = false;

  @action
  toggleTestMicrophoneEnabled = () => {
    this.testMicrophoneEnabled = !this.testMicrophoneEnabled;
  };

  @action
  clearAllData = async () => {
    try {
      this.phoneCalls.forEach((pc) => {
        // only runs this if the users had made any call or recieved any call
        if (
          pc.session &&
          (pc.session.state === 'Established' ||
            pc.session.state === 'Establishing')
        ) {
          void pc.session.bye();
        }

        if (pc.isCallConnecting) {
          pc.clearKeyPress();
          pc.setShowDialPad(false);
          pc.muteIconState('microphone');
          pc.setIsCallMuted(false);
          pc.onHoldIconState('pause circle outline');
          pc.setIsCallOnHold(false);
          pc.setIsMediaConnecting(false);
          pc.setIsCallConnected(false);
          pc.setIsCallConnecting(false);
          pc.setIsMediaConnected(false);
          pc.ringtone.pause();
          pc.ringtone.currentTime = 0;
          pc.clearPersonAndContact();
          pc.resetDisplay();
          void pc.session.bye();
        }
        // unregistering Sip of the user

        pc.clearPersonAndContact();
      });
    } catch (error) {
      console.error(error);
      logError(
        error,
        'Unkown issue, hard reload happening',
        'PhoneStore',
        'clearAllData'
      );
      sendIpcLog('info', 'Window was automatically reloaded', {
        reason: 'error in PhoneStore.clearAllData()',
        error
      });
      // If nothing works, Hard Reload
      location.reload();
    }
    if (this.ua?.isConnected()) await this.ua.stop();

    if (this.registerer?.state === RegistererState.Registered)
      await this.registerer.unregister();

    // wont register back unless the person logs In again
    //TODO if this is still needed
    when(
      () =>
        !this.rootStore.personStore.IsLoggedIn &&
        this.rootStore.configStore.signedInPersonConfig === null,
      () => {
        //  we may no longer need this, but I will leave it here for now. and checkt he behaviour
        //this.runRegister();
      }
    );
  };

  @observable
  public sessionUIs: {};

  @observable
  public phoneCalls: IObservableArray<PhoneCallModel> = observable.array();

  @action
  public setPhoneCalls = (phoneCallModel: PhoneCallModel) => {
    this.phoneCalls.push(phoneCallModel);
  };

  @observable
  public incomingPhoneCalls: IObservableArray<PhoneCallModel> =
    observable.array();

  @action
  public setIncomingCalls = (phoneCallModel: PhoneCallModel) => {
    this.incomingPhoneCalls.push(phoneCallModel);
  };

  @observable
  public warmTransferOld: PhoneCallModel;

  @action
  public setWarmTransferOld = (warmTransferOld: PhoneCallModel) => {
    this.warmTransferOld = warmTransferOld;
  };

  private ua: UserAgent;
  private sipTransport: Transport;

  @observable
  private maxReconnectionAttempts = 6;

  @action
  setMaxReconnectionAttempts = (maxReconnectionAttempts: number) =>
    (this.maxReconnectionAttempts = maxReconnectionAttempts);

  @action
  public addingMaxReconnectionAttempts = () => {
    this.setMaxReconnectionAttempts(this.maxReconnectionAttempts + 1);
  };

  public reconnectTimer;

  @observable
  isThereDeviceChange = false;

  @action
  setIsThereDeviceChange = (isThereDeviceChange: boolean) => {
    this.isThereDeviceChange = isThereDeviceChange;
  };

  @observable
  registerer: Registerer = null;

  handleRegisterSip = () => {
    this.registerer = new Registerer(this.ua);
    // Setup registerer state change handler
    this.registerer.stateChange.addListener((newState) => {
      switch (newState) {
        case RegistererState.Registered:
          this.setWebRTCRegistrationConcluded(true);
          break;
        case RegistererState.Unregistered:
          this.setWebRTCRegistrationConcluded(false);
          break;
        case RegistererState.Terminated:
          this.setIsWebRTCConnected(false);
          break;
      }
    });
  };

  private getPersonByRemoteIdentityUser = async (
    user: string,
    host: string
  ): Promise<ContactModelV2 | null> => {
    if (isPhoneNumber(user)) return null;

    // else if user is ext. username can be searched in BV directory
    return searchPersonBySip(`${user}@${host}`);
  };

  /**
   * runRegister does two things
   * 1) whenever the user login it registered with the SIP in the server and whenever Logs out it unregisters the user
   * 2) creates invite for incoming calls
   * SIP invite uses the `session.request` from the header that is sent by FreeSwitch
   */
  @action
  public runRegister() {
    // Guard: if the new softphone is the active preference, legacy SIP must not register.
    const coordinator = SoftphoneCoordinator.getInstance();
    const personId = this.rootStore.personStore.loggedInPersonId;
    const isNewSoftphoneEnabled = coordinator.readPreferenceSync(personId);
    if (isNewSoftphoneEnabled) {
      return;
    }

    when(
      () =>
        this.rootStore.personStore.IsLoggedIn &&
        this.rootStore.configStore.signedInPersonConfig !== null &&
        this.rootStore.configStore.signedInPersonConfig.state === 'fulfilled',
      async () => {
        // We don't want to cancel active/incoming calls
        if (this.AnyPhoneConnectionActive) {
          return;
        }

        if (this.ua?.isConnected()) await this.ua.stop();

        if (this.registerer?.state === RegistererState.Registered)
          await this.registerer.unregister();

        const loadCfgStatus = this.rootStore.configStore.signedInPersonConfig;
        if (loadCfgStatus.state === 'fulfilled') {
          const webRtcConfig = loadCfgStatus.value.data.webRtc;
          if (!NODE_ENV_PRODUCTION) {
            webRtcConfig.traceSip = true;
          }

          const loggedInUserInfo = usePersonStore.getState();

          let loggedInCallerId: string;
          const lpLines = loggedInUserInfo.lines ?? [];
          for (let i = 0; i < lpLines.length; i++) {
            if (lpLines[i].id === loggedInUserInfo.primaryLineId) {
              if (lpLines[i].callerId) {
                loggedInCallerId = lpLines[i].callerId;
              } else {
                const plId = loggedInUserInfo.primaryLineId ?? '';
                loggedInCallerId = plId.toString();
              }
            }
          }
          if (isEmpty(loggedInCallerId)) {
            loggedInCallerId = 'Unknown Number';
          }
          const url = webRtcConfig.uri?.replace(/\s/g, '');
          const uri: URI = UserAgent.makeURI(
            webRtcConfig.uri?.includes('sip:') ? url : 'sip:' + url
          );
          if (!uri) {
            useNotificationStore.getState().addNotify({
              type: 'error',
              dismissible: true,
              title: 'Wrong webRtc configuration',
              message:
                'URI for making sip connection is wrong. Please contact the support.',
            });

            return;
          }
          const configuration: UserAgentOptions = {
            uri: uri,
            authorizationUsername: webRtcConfig.authorizationUser,
            authorizationPassword: webRtcConfig.password,
            logBuiltinEnabled: !NODE_ENV_PRODUCTION && !NODE_ENV_TEST,
            logLevel: NODE_ENV_PRODUCTION ? 'warn' : 'debug',
            transportOptions: {
              connectionTimeout: TRANSPORT_TIMEOUT.CONNECTION,
              traceSip: !NODE_ENV_PRODUCTION && !NODE_ENV_TEST,
              wsServers: webRtcConfig.wsServers,
              maxReconnectionAttempts: TRANSPORT_TIMEOUT.MAX_ATTEMPTS,
              reconnectionTimeout: TRANSPORT_TIMEOUT.RECONNECTION,
            },
            sessionDescriptionHandlerFactoryOptions: {
              constraints: {
                audio: true, // {deviceId: this.microphoneId ? {exact: this.microphoneId} : 'default'}
                video: false,
              },
              iceCheckingTimeout: 5000,
              rtcConfiguration: {
                rtcpMuxPolicy: 'require',
                // iceServers: [
                //     {
                //         urls: '206.15.174.53:63478'
                //     }
                // ]
              },
            },
            userAgentString: SIP_USER_AGENT,
            delegate: {
              onInvite: (invitation) => {
                void (async () => {
                  const presenceStatus =
                    this.rootStore.uiStore.selectPersonPresenceStatus(
                      this.rootStore.personStore.loggedInPersonId.toString()
                    ).state;

                  let loggedInCallerId: string;

                  for (let i = 0; i < loggedInUserInfo.lines.length; i++) {
                    if (
                      loggedInUserInfo.lines[i].id ===
                      loggedInUserInfo.primaryLineId
                    ) {
                      if (loggedInUserInfo.lines[i].callerId) {
                        loggedInCallerId = loggedInUserInfo.lines[i].callerId;
                      } else {
                        loggedInCallerId =
                          loggedInUserInfo.primaryLineId.toString();
                      }
                    }
                  }
                  if (isEmpty(loggedInCallerId)) {
                    loggedInCallerId = 'Unknown Number';
                  }
                  let phoneNumber = '';

                  const newCall = new PhoneCallModel(
                    this.rootStore,
                    this,
                    loggedInCallerId,
                    this.rootStore.personStore.loggedInAccountId,
                    this.rootStore.personStore.loggedInPersonId,
                    invitation,
                    null,
                    null,
                    null,
                    this.localRemoteAudio,
                    this.localRingTone,
                    this.localIncomingTone,
                    this.secondLocalIncomingtone,
                    this.lostConnectionTone,
                    this.localCallDisconnectedTone
                  );

                  const fromSip = await this.getPersonByRemoteIdentityUser(
                    invitation.remoteIdentity.uri.user,
                    invitation.remoteIdentity.uri.host
                  );

                  if (fromSip) {
                    const callerIdName = invitation.remoteIdentity.displayName;
                    const personId = fromSip.sourceId;

                    if (callerIdName) {
                      newCall.setCallerIdName(callerIdName);
                    }
                    if (personId !== undefined) {
                      const phoneUri = `pr${personId}@${invitation.remoteIdentity.uri.host}`;
                      newCall.setPerson(
                        this.rootStore.personStore.loadPersonByIdGetIfMissingGet(
                          personId
                        )
                      );
                      if (
                        this.enableIncomingCalls &&
                        newCall.person &&
                        presenceStatus !== 'DoNotDisturb'
                      ) {
                        void newCall.person.then((p) => {
                          this.addIncomingCallWebNotification(
                            p.lines?.length > 0
                              ? formatNumberWithNationalCode(p.lines[0].number)
                              : '',
                            p.name,
                            newCall.session.id
                          );
                        });
                      }

                      const uri = UserAgent.makeURI(
                        'sip:' + phoneUri.replace(/\s/g, '')
                      );
                      newCall.setPhoneURI(uri);
                    }
                  } else {
                    // Incoming Calls only
                    const callerIdName =
                      invitation.request.from.displayName || '';

                    if (callerIdName) {
                      newCall.setCallerIdName(callerIdName);
                    }

                    if (invitation.remoteIdentity.uri instanceof URI) {
                      phoneNumber = invitation.remoteIdentity.uri.user;

                      if (!isNaN(phoneNumber as any)) {
                        const contactsWithPhoneNumber =
                          await fetchContactsWithPhoneNumber(phoneNumber);

                        if (contactsWithPhoneNumber?.length) {
                          const firstContact = contactsWithPhoneNumber.shift();
                          newCall.setContact(firstContact);
                          newCall.setCallerIdName(firstContact.name);

                          if (contactsWithPhoneNumber.length) {
                            newCall.setOtherContacts(contactsWithPhoneNumber);
                          }
                        }

                        if (
                          this.enableIncomingCalls &&
                          newCall.contact &&
                          presenceStatus !== 'DoNotDisturb'
                        ) {
                          this.addIncomingCallWebNotification(
                            phoneNumber,
                            newCall.contact?.name || callerIdName,
                            newCall.session.id
                          );
                        }
                      }

                      newCall.setPhoneNumber(phoneNumber);
                      const uri = invitation.remoteIdentity.uri.host?.includes(
                        'sip:'
                      )
                        ? invitation.remoteIdentity.uri.host
                        : `sip:${phoneNumber}@${invitation.remoteIdentity.uri.host}`;
                      const phoneNumUri = UserAgent.makeURI(
                        `sip:${uri.replace(/\s/g, '')}`
                      );
                      newCall.setPhoneURI(phoneNumUri);
                    }
                  }

                  newCall.startOfCall = moment.utc().format().toString();
                  newCall.setIncomingCall(true);
                  const secondIncomingTone = newCall.secondIncomingTone;

                  const isOnCallStatus =
                    this.rootStore.uiStore.selectPersonPresenceStatus(
                      this.rootStore.personStore.loggedInPersonId.toString()
                    ).state === 'OnCall';

                  if (presenceStatus !== 'DoNotDisturb' && newCall?.session) {
                    this.setIncomingCalls(newCall);
                    if (this.phoneCalls.length < 1 && !isOnCallStatus) {
                      try {
                        const NEW_CALL_RINGTONE_VOLUME = 0.49;
                        newCall.incomingTone.volume = NEW_CALL_RINGTONE_VOLUME;

                        await newCall.incomingTone.play();
                      } catch (error) {
                        console.warn('Unable to play incoming tone.', error);

                        const errorName = error?.name || 'Unknown';
                        const errorMessage =
                          error?.message || 'No error message';

                        /* Not allowed error is thrown when play() is considered an autoplay
                          and the browser is blocking that until the user interacts with the page.
                          Not supported error is thrown when audio formarts are not supported.
                          Since we only use .mp3 this error will only happen in certain scenarios that we cannot control.
                          Both This errors will be ignored
                           */
                        const isExpectedError =
                          errorName === 'NotAllowedError' ||
                          errorName === 'NotSupportedError';

                        // Send logs for issues with the audio device
                        if (isExpectedError) {
                          this.logger.error(
                            `Incoming tone error: ${errorName}; Message: ${errorMessage}; Timestamp: ${new Date().toISOString()}`
                          );
                        } else {
                          // Only notify bugsnag for unexpected errors
                          bugsnagClient.notify(error, (event) => {
                            event.severity = 'error';
                            event.context = 'incomingCall';
                          });
                        }
                      }
                      secondIncomingTone.pause();
                    } else {
                      newCall.incomingTone.pause();
                      void secondIncomingTone.play();
                      secondIncomingTone.volume = 1;
                      let timeout;
                      secondIncomingTone.ontimeupdate = () => {
                        if (
                          !newCall.isIncomingCall ||
                          this.checkIfOtherCallsAreOnHold()
                        ) {
                          clearTimeout(timeout);
                        }
                      };
                      secondIncomingTone.onended = () => {
                        timeout = setTimeout(() => {
                          secondIncomingTone.currentTime = 0;
                          void secondIncomingTone.play();
                        }, TIME_CALL_ALERT_INTERVAL_MS);
                      };
                    }
                  }

                  newCall.setSipFromUri(
                    invitation.remoteIdentity.uri.toString()
                  );
                  newCall.setSipToUri(
                    invitation.localIdentity?.uri?.toString() || ''
                  );
                })();
              },
            },
          };

          await this.establishTransportConnection(configuration);
        }
      }
    );
  }

  checkIfOtherCallsAreOnHold = () => {
    return this.phoneCalls
      .slice(0, -1)
      .every((phoneCall) => phoneCall.isCallOnHold);
  };

  handleTransportEvents = (ua: UserAgent, configuration: UserAgentOptions) => {
    ua.transport.onConnect = () => {
      this?.pauseLostConnectionTone();
      this.setIsWebRTCConnected(true);
      this.handleRegisterSip();

      this.updateEnableIncomingCalls();

      // clear re-connect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.setIsWebRTCReconnecting(false);
      }
    };

    ua.transport.onDisconnect = (error?: Error) => {
      logError(error, 'UA Disconnected', 'PhoneStore', 'onDisconnect', 'info');
      this.setIsWebRTCConnected(false);
      this.setIsWebRTCReconnecting(false);
    };

    this.setSIPKeepAlive();
  };

  /**
   * Establishes a connection to the transport layer using the provided configuration.
   *
   * @param configuration - The configuration object for the UserAgent.
   * @returns The initialized UserAgent instance.
   */
  establishTransportConnection(
    configuration: UserAgentOptions['transportOptions']
  ): void {
    // Create a new UserAgent instance with the provided configuration
    const userAgent = new UserAgent(configuration);

    // Handle transport events for the new UserAgent instance
    this.handleTransportEvents(userAgent, configuration);

    userAgent.start().catch((error) => {
      const currentError = error as Error;
      logError(
        currentError,
        currentError.message,
        'PhoneStore',
        'establishTransportConnection',
        'error'
      );
    });

    this.ua = userAgent;
  }

  @observable
  enableIncomingCalls = true;

  @action
  setEnableIncomingCalls = (enableIncomingCalls: boolean) => {
    this.enableIncomingCalls = enableIncomingCalls;
    this.onToggleOnlyIncomingCalls(enableIncomingCalls);
    void localforage.setItem<boolean>(SHOW_INCOMING_CALL, enableIncomingCalls);
  };

  updateEnableIncomingCalls = () => {
    void localforage
      .getItem<boolean>(SHOW_INCOMING_CALL)
      .then((isIncomingCall) => {
        runInAction(() => {
          if (!isNullOrUndefined(isIncomingCall)) {
            this.setEnableIncomingCalls(isIncomingCall);
          } else {
            this.setEnableIncomingCalls(true);
          }
        });
      });
  };

  onToggleOnlyIncomingCalls = (enableIncomingCalls: boolean) => {
    if (!this.registerer) {
      return;
    }
    if (
      enableIncomingCalls &&
      this.registerer.state !== RegistererState.Registered
    ) {
      void this.registerer.register();
    } else if (
      !enableIncomingCalls &&
      this.registerer.state === RegistererState.Registered
    ) {
      void this.registerer.unregister();
    }
  };

  /**
   * Reconnects SIP connection with improved error handling
   * This method handles both simple transport reconnection and full SIP re-registration
   */
  @action
  reconnectSip = async (): Promise<void> => {
    try {
      // If we don't have a UserAgent or it's not properly initialized,
      // trigger a full re-registration which will wait for config to be available
      if (!this.ua || !this.ua.transport) {
        this.runRegister();
        return;
      }

      // If we have a UserAgent but transport is disconnected, try to reconnect
      if (this.ua.transport.state !== 'Connected') {
        await this.ua.transport.connect();
      }

      // If transport is connected but not registered, handle registration
      if (
        this.ua.transport.state === 'Connected' &&
        this.registerer?.state !== RegistererState.Registered
      ) {
        this.handleRegisterSip();
      }
    } catch (error) {
      this.logger.error('PhoneStore reconnectSip failed', error as Error);

      // If reconnection fails, fallback to full re-registration
      this.runRegister();
    }
  };

  @observable
  isWebrtcAttemptDone = false;

  @action
  setIsWebrtcAttemptDone = (isWebrtcAttemptDone: boolean) => {
    this.isWebrtcAttemptDone = isWebrtcAttemptDone;
  };

  private addIncomingCallWebNotification = (
    phoneNumber: string,
    displayName: string,
    callId: string
  ) => {
    console.debug('addIncomingCallWebNotification', phoneNumber, displayName);
    const displayNameIsNumber = PhoneNumberUtil.isPossibleNumberString(
      displayName,
      'US'
    );

    this.rootStore.notificationStore.addWebNotification(
      'New incoming call',
      {
        tag: WN_INCOMING_CALL_PREFIX + callId,
        requireInteraction: false,
        timeout: 30000,
        icon: !IS_ELECTRON ? PHONE_INCOMING_128 : PHONE_INCOMING_128_S3, // For Electron, use a publicly available URL for Windows desktop notifications
        silent: true,
        body: `There is an incoming call from ${
          !displayNameIsNumber ? displayName + '\r\n' : ''
        }${!displayNameIsNumber ? phoneNumber : displayName}`,
      },
      'info',
      false
    );
  };

  @observable sipKeepAlive: ReturnType<typeof setInterval> = null;
  @action setSIPKeepAlive = () => {
    this.sipKeepAlive = setInterval(() => {
      if (this.ua.transport.state === 'Disconnected') {
        void this.ua.transport.connect();
      }
    }, 15000);
  };

  @observable isAttemptingToReconnectSip = false;
  @action setIsAttemptingToReconnectSip = (
    isAttemptingToReconnectSip: boolean
  ) => {
    this.isAttemptingToReconnectSip = isAttemptingToReconnectSip;
  };

  @action resetTestWebRTCConnected = () => {
    clearInterval(this.setTimer);
    this.setWebrtcCounter(CONNECTION_RETRY.COUNTER_INITIAL);
    this.setWebRTCTimer(CONNECTION_RETRY.TIMER_INITIAL);
    this.setIsAttemptingToReconnectSip(false);
  };

  setTimer;
  // This async will garantee to run the Sip registery before making the calls
  testWebRTCConnected = async (): Promise<boolean> =>
    new Promise((resolve) => {
      if (!this.ua) {
        /**
         * Softphone Coordination Legacy/New
         * Show different notification messages based on which softphone version is active.
         * New softphone: inform user the feature is not yet supported.
         * Legacy softphone: show error and offer page refresh.
         */
        const coordinator = SoftphoneCoordinator.getInstance();
        const personId = this.rootStore.personStore?.loggedInPersonId;
        const isNewSoftphoneEnabled = coordinator.readPreferenceSync(personId);

        if (!isNewSoftphoneEnabled) {
          useNotificationStore.getState().addNotify({
            type: 'error',
            dismissible: true,
            title: 'Error Starting Call',
            message:
              "It wasn't possible to start the call. Please refresh the page.",
            action: { label: 'Refresh', onClick: () => location.reload() },
          });

          bugsnagClient.notify(
            'CUU2-2648 User agent is undefined when placing a call.',
            (event) => {
              event.severity = 'warning';
              event.context = 'PhoneStore';
              event.addMetadata('custom', {
                function: 'testWebRTCConnected',
              });
            }
          );
        }

        return resolve(false);
      }

      this.ua.transport.state === 'Disconnected' &&
        void this.ua.transport.connect();

      this.setTimer = setInterval(() => {
        if (this.isWebRTCConnected) {
          this.resetTestWebRTCConnected();
          return resolve(true);
        }

        this.setIsAttemptingToReconnectSip(true);
        this.setWebRTCTimer(this.webRTCTimer - 1);
        if (this.webRTCTimer < 0 && this.webrtcCounter < 6) {
          this.setWebRTCTimer(5);
          this.setWebrtcCounter(this.webrtcCounter + 1);
          this.ua.transport.state === 'Disconnected' &&
            void this.ua.transport.connect();
        }
        if (this.webRTCTimer === 0 && this.webrtcCounter === 6) {
          this.resetTestWebRTCConnected();
          this.setIsWebrtcAttemptDone(true);
          setTimeout(
            () => this.setIsWebrtcAttemptDone(false),
            CONNECTION_RETRY.TIMEOUT_MS
          );
        }
      }, TIME_SECOND_MS);
    });

  /**
   * This function passes the information to `Call` function
   * If the microphone Permission is not there, it wont even make a call
   */
  @action
  callWithPerson = async (
    personId?: string,
    phone?: string,
    isWarmtransfer?: boolean
  ) => {
    // Check if new softphone is enabled - if so, don't execute legacy softphone logic
    const loggedInPersonId = this.rootStore.personStore.loggedInPersonId;
    if (loggedInPersonId) {
      const coordinator = SoftphoneCoordinator.getInstance();
      const isNewSoftphoneEnabled =
        coordinator.readPreferenceSync(loggedInPersonId);
      if (isNewSoftphoneEnabled) {
        console.log(
          '[PhoneStore] New softphone is enabled, skipping legacy callWithPerson'
        );
        return;
      }
    }

    await this.refreshMicrophonePermissionStatus('outgoing');
    if (this.browserMicrophonePermissionState !== 'granted') {
      return;
    }

    // we do promise for making sure that if WEBRTC is not Registered Yet, We register it before making a dial. Doing so always guarantee that we wont lose all the information we need to call
    const isWebRTCConnected =
      this.isWebRTCConnected || (await this.testWebRTCConnected());
    if (!isWebRTCConnected) return;

    try {
      /*
      To align behavior accross platforms, directory takes precedence,
      When we don't receive a personId, we check directory for an existing person by phone number
      and return its personId, so we can display the right call destinatary name
      */
      if (personId) {
        this.call(
          personId,
          this.rootStore.personStore.loadPersonByIdGetIfMissingGet(personId),
          null,
          null,
          isWarmtransfer,
          null,
          null,
          null
        );
      } else if (phone !== undefined && phone !== null && !isEmpty(phone)) {
        let directoryMatchId: number;

        // If not a phone number (we assume it's an extension), search the directory for a user with that extension
        if (!isPhoneNumber(phone)) {
          const domain = (await this.rootStore.configStore.signedInPersonConfig)
            ?.data?.webRtc?.domain;

          if (domain) {
            const extensionMatch = await searchPersonBySip(
              `${phone}@${domain}`
            );

            if (extensionMatch) {
              directoryMatchId = Number.parseInt(extensionMatch.sourceId);
            }
          }
        }

        let contacts: ContactModelV2[] = null;

        if (
          !directoryMatchId &&
          !phone.startsWith('c/') &&
          !phone.startsWith('Tran') &&
          !/[*ABCD]/g.test(phone)
        ) {
          contacts = await fetchContactsWithPhoneNumber(phone);
        }

        this.call(
          null,
          // Get user info from the directory or sip search above
          directoryMatchId
            ? this.rootStore.personStore.loadPersonByIdGetIfMissingGet(
                directoryMatchId.toString()
              )
            : null,
          phone,
          contacts,
          isWarmtransfer,
          null,
          null,
          null
        );
      } else {
        throw new Error('Either Phone Number or Person Id is required.');
      }
    } catch (e) {
      if (e.message === 'Either Phone Number or Person Id is required.') {
        logError(e, '', 'PhoneStore', 'callWithPerson');
        throw e;
      }
    }
  };

  /**
   * Builds the actual sip information and passes all of the info to the PhoneCallModel
   */
  @action
  public call(
    personId?: string,
    person?: IPromiseBasedObservable<ContactModelV2>,
    phoneNumber?: string,
    contacts?: ContactModelV2[],
    isWarmtransfer?: boolean,
    isMerge?: boolean,
    conferenceId?: string,
    callback?: Function
  ) {
    const loggedInUserInfo = usePersonStore.getState();

    let loggedInCallerId: string;
    for (let i = 0; i < loggedInUserInfo.lines.length; i++) {
      if (loggedInUserInfo.lines[i].id === loggedInUserInfo.primaryLineId) {
        loggedInCallerId = loggedInUserInfo.primaryLineId.toString();
      }
    }
    if (isEmpty(loggedInCallerId)) {
      loggedInCallerId = 'Unknown Number';
    }

    // Check make sure it has atleast one of them available
    if (
      (!personId && isEmpty(phoneNumber)) ||
      (personId && !isEmpty(phoneNumber))
    ) {
      throw new Error('Call requires either personId or phone, but not both.');
    }
    let phoneUriPrefix;
    if (personId) {
      phoneUriPrefix = 'pr' + personId;
    } else if (isMerge) {
      phoneUriPrefix = phoneNumber;
    } else {
      phoneUriPrefix = phoneNumber.replace(/[() -]/g, '');
    }
    if (this.rootStore.configStore.signedInPersonConfig.state === 'fulfilled') {
      const uri = phoneUriPrefix.includes('sip:')
        ? phoneUriPrefix
        : 'sip:' +
          phoneUriPrefix +
          '@' +
          this.rootStore.configStore.signedInPersonConfig.value.data.webRtc
            .domain;
      const completeUri = UserAgent.makeURI(uri.replace(/\s/g, ''));
      const inviter = new Inviter(this.ua, completeUri, { earlyMedia: true });

      const contact = contacts?.length ? contacts.shift() : undefined;
      const otherContacts = contacts?.length ? contacts : undefined;

      const newOutBoundCall = new PhoneCallModel(
        this.rootStore,
        this,
        loggedInCallerId,
        this.rootStore.personStore.loggedInAccountId,
        this.rootStore.personStore.loggedInPersonId,
        inviter,
        completeUri,
        person,
        contact,
        this.localRemoteAudio,
        this.localRingTone,
        this.localIncomingTone,
        this.secondLocalIncomingtone,
        this.lostConnectionTone,
        this.localCallDisconnectedTone,
        isWarmtransfer,
        isMerge,
        conferenceId,
        this.handleAccept,
        callback,
        undefined,
        undefined,
        otherContacts,
        phoneNumber
      );

      newOutBoundCall.startOfCall = moment.utc().format().toString();
      newOutBoundCall.setIncomingCall(false);
      newOutBoundCall.setPhoneNumber(phoneNumber);
      this.setPhoneCalls(newOutBoundCall);
      if (this.phoneCalls.length > 1) {
        // Localy keeping track of the newest session
        this.setOtherSessionsOnHold(inviter);
      }

      newOutBoundCall.setSipToUri(completeUri.toString());
      newOutBoundCall.setSipFromUri(
        this.ua.configuration.uri?.toString() || ''
      );

      return newOutBoundCall.session;
    }
  }

  handleAccept = () => {
    this.configureMicrophone(this.microphoneId);
    this.configureSpeaker(this.speakerId);
  };

  public getUserMediaFailure = (e) =>
    logError(e, 'getUserMedia failed:', 'PhoneStore', 'getUserMediaFailure');

  @action
  public guidGenerator = () => uuid();

  @observable
  public numberOfPeopleInARoom: number;

  @action
  public setNumberOfPeopleInARoom = (numberOfPeopleInARoom: number) =>
    (this.numberOfPeopleInARoom = numberOfPeopleInARoom);

  public cRoom = 'c/room';
  @action
  transferToConference = () => {
    let i = 0;
    let guid;
    if (!this.AnyPhoneCallHasConferenceId) {
      guid = this.guidGenerator();

      this.conferenceRoomsIdAndNumbers.set(guid, observable.array());
    }
    let number: string;
    this.rootStore.configStore.signedInPersonConfig.case({
      fulfilled: (resp) => {
        if (this.AnyPhoneCallHasConferenceId && this.ConferenceIdGuid) {
          this.ActivePhoneCall.conferenceId = this.ConferenceIdGuid;

          this.pushIdOrNumberIntoConferenceRoom(this.ActivePhoneCall.callUri);
          const url = `sip:${this.cRoom}${this.ConferenceIdGuid}/noentersound=true,nomoh=true@${resp.data.webRtc.domain}`;
          const uri = UserAgent.makeURI(url.replace(/\s/g, ''));
          void this.ActivePhoneCall.session.refer(uri);
          return;
        }
        this.call(
          null,
          null,
          `sip:${this.cRoom}${guid}/noentersound=true,nomoh=true,mintwo=false,endconf=true@${resp.data.webRtc.domain}`,
          [this.ActivePhoneCall.contact],
          false,
          true,
          guid,
          (phoneCalls) => {
            phoneCalls
              .filter((p) => !p.callUri?.includes(this.cRoom))
              .forEach((element) => {
                if (!element?.callUri?.includes('pr')) {
                  number = element?.callUri?.split('@')[0];
                  if (number.includes('sip:')) {
                    const callUriArray = number.split('sip:');
                    number = callUriArray[callUriArray.length - 1];
                  }
                  element.setMergeTransfer(true);

                  this.pushIdOrNumberIntoConferenceRoom(number);
                } else if (element?.callUri?.includes('pr')) {
                  element.setMergeTransfer(true);
                  const callUri = element?.callUri?.includes('sip:')
                    ? element?.callUri?.split('sip:')[1]
                    : element?.callUri;

                  this.pushIdOrNumberIntoConferenceRoom(callUri);
                  i++;
                }
                element.conferenceId = this.ConferenceIdGuid;
                const url = `sip:${this.cRoom}${this.ConferenceIdGuid}/noentersound=true,nomoh=true@${resp.data.webRtc.domain}`;
                const uri = UserAgent.makeURI(url.replace(/\s/g, ''));
                element.session.refer(uri);
              });
          }
        );
      },
    });
  };

  @action
  pushIdOrNumberIntoConferenceRoom = (id: string) => {
    this.conferenceRoomsIdAndNumbers.get(this.ConferenceIdGuid)?.push(id);

    // Make the change noticeable by useEffect
    this.conferenceRoomsIdAndNumbers = observable.map(
      new Map(this.conferenceRoomsIdAndNumbers)
    );
  };

  selectPeopleOrContactsFromConferenceRoom = (conferenceId: string) => {
    return this.conferenceRoomsIdAndNumbers
      .get(conferenceId)
      .map(async (value) => {
        let number;

        if (value.includes('pr')) {
          number = value.split('pr').pop().split('@')[0];
          const loadPersonPbo =
            await this.rootStore.personStore.loadPersonByIdGetIfMissingGet(
              number
            );

          return loadPersonPbo;
        } else {
          number = value.split('@')[0];

          if (!number) {
            return null;
          }

          let loadContact: ContactModelV2 | ContactModelBase =
            this.rootStore.personStore.getAllContactsWithSameNum(number)?.case({
              fulfilled: (contacts) => {
                return contacts[0];
              },
            });

          if (!loadContact) {
            loadContact =
              await this.rootStore.contactStore.loadContactByPhoneNumber(
                number
              );
          }

          return loadContact;
        }
      });
  };

  @computed
  get AnyPhoneConnectionActive() {
    return (
      this.phoneCalls.some((p) => this.isPhoneConnectionActive(p)) ||
      this.incomingPhoneCalls.some((p) => this.isPhoneConnectionActive(p))
    );
  }

  refreshMicrophonePermissionStatus = async (
    callType: null | 'incoming' | 'outgoing'
  ) => {
    if (!IS_ELECTRON && navigator?.permissions) {
      if (navigator.mediaDevices?.getUserMedia) {
        try {
          const localStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          this.setBrowserMicrophonePermissionState('granted');
          //@ts-ignore
          window.localStream = localStream;
          stopMediaStream(window.localStream);
          this.checkForIODevices();
          return;
        } catch (err) {
          this.setBrowserMicrophonePermissionState('denied');
          logError(
            err,
            err.message,
            'PhoneStore',
            'browserMicrophonePermissionState'
          );
          if (callType) {
            const microphoneNotificationMessageCopy = {
              incoming: {
                title: 'Allow access to your microphone',
                description:
                  'Please enable microphone access in your browser settings to receive calls.',
              },
              outgoing: {
                title: 'Allow access to your microphone',
                description:
                  'Please enable microphone access in your browser settings to make calls.',
              },
              default: {
                title: 'Allow access to your microphone',
                description:
                  'Please enable microphone access in your browser settings to make and receive calls.',
              },
            }[callType || 'default'];

            useNotificationStore.getState().addNotify({
              title: microphoneNotificationMessageCopy.title,
              message: microphoneNotificationMessageCopy.description,
              type: 'info',
              autoDismiss: true,
              dismissible: 'button',
            });
          }
          return;
        }
      } else {
        console.warn('getUserMedia not supported');
        this.setBrowserMicrophonePermissionState('denied');
        return;
      }
    } else {
      // must assume granted since we can't query :(, when we upgrade sip we can do better
      // [BC-646] Electron environments always sink granted
      this.setBrowserMicrophonePermissionState('granted');
      return;
    }
  };

  isPhoneConnectionActive = (phoneCall) =>
    phoneCall.isCallConnecting ||
    phoneCall.isCallConnected ||
    phoneCall.isIncomingCall ||
    phoneCall.isMediaConnecting;

  /**
   *  The call that is active and NOT on HOLD
   *
   *  returns `undefined` if nothing is found
   */
  @computed
  get ActivePhoneCall() {
    // The newest Session , isTransferMode set to false
    return this.phoneCalls[this.phoneCalls.length - 1];
  }

  @computed
  get IsActiveCall() {
    return (
      this.AnyPhoneConnectionActive ||
      this.isCallTimeout ||
      this.rootStore.conversationStore.listOfIncomingVideoCalls.size > 0
    );
  }

  /**
   * Cleans up audio elements by removing their source objects
   * @param audioElements Object containing audio elements to clean
   */
  private cleanupAudioElements(audioElements: {
    localRemoteAudio: IHTMLAudioElement;
    localRingTone: IHTMLAudioElement;
    localIncomingTone: IHTMLAudioElement;
    secondLocalIncomingtone: IHTMLAudioElement;
    lostConnectionTone: IHTMLAudioElement;
    disconnectedTone: IHTMLAudioElement;
  }): void {
    Object.values(audioElements).forEach((element: HTMLAudioElement) => {
      if (element?.srcObject) {
        element.srcObject = null;
      }
    });
  }

  /**
   * Cleans up call-related states and resets them to default values
   */
  private resetCallStates(): void {
    this.localSipSession = null;
    this.outBound = false;
    this.warmTransferOld = null;

    // Clear conference states
    this.conferenceRoomsIdAndNumbers.clear();
    this.setExistingConferenceIdId(null);

    // Reset WebRTC states
    this.setIsWebRTCReconnecting(false);
    this.setIsInternetConnectedOnActiveCall(true);
    this.setIsCallTimeout(false);
  }

  @action
  cancelCallOnNoWebRTC = (): void => {
    this.setIsAttemptingToReconnectSip(false);
    this.setIsWebrtcAttemptDone(false);
    this.setWebrtcCounter(CONNECTION_RETRY.COUNTER_INITIAL);
    this.setWebRTCTimer(CONNECTION_RETRY.TIMER_INITIAL);

    clearInterval(this.setTimer);

    // Fist pause the sound of connection lost
    this.pauseLostConnectionTone();

    // Then play the tone of call disconnected
    this.playDisconnectedTone();

    [...this.incomingPhoneCalls, ...this.phoneCalls].forEach((call) =>
      call.networkTermination()
    );
    this.phoneCalls = observable.array();
    this.incomingPhoneCalls = observable.array();

    // Cleanup audio elements
    this.cleanupAudioElements({
      localRemoteAudio: this.localRemoteAudio,
      localRingTone: this.localRingTone,
      localIncomingTone: this.localIncomingTone,
      secondLocalIncomingtone: this.secondLocalIncomingtone,
      lostConnectionTone: this.lostConnectionTone,
      disconnectedTone: this.localCallDisconnectedTone,
    });

    // Clear SIP keepalive interval
    if (this.sipKeepAlive) {
      clearInterval(this.sipKeepAlive);
      this.sipKeepAlive = null;
    }

    // Reset all call-related states
    this.resetCallStates();
  };

  @observable
  isInternetConnectedOnActiveCall = true;

  @action
  setIsInternetConnectedOnActiveCall = (
    isInternetConnectedOnActiveCall: boolean
  ) => (this.isInternetConnectedOnActiveCall = isInternetConnectedOnActiveCall);

  @observable
  isCallTimeout = false;

  @action
  setIsCallTimeout = (isTimeoutOver: boolean) =>
    (this.isCallTimeout = isTimeoutOver);

  @observable
  setTimeOut: any;

  @action
  playLostConnectionTone = () => {
    if (!this.lostConnectionTone) return;

    this.lostConnectionTone.currentTime = 0; // Reset the tone to the beginning
    this.lostConnectionTone.volume = 0.5;
    void this.lostConnectionTone.play().catch((error: Error) => {
      console.warn('Unable to play lost connection tone.', error);

      const errorName = error?.name || 'Unknown';
      const errorMessage = error?.message || 'No error message';

      const isExpectedError =
        errorName === 'NotAllowedError' || errorName === 'NotSupportedError';

      if (isExpectedError) {
        this.logger.error(
          `Lost connection tone error: ${errorName}; Message: ${errorMessage}; Timestamp: ${new Date().toISOString()}`
        );
      } else {
        bugsnagClient.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'lostConnection';
        });
      }
    });
  };

  @action
  pauseLostConnectionTone = () => {
    if (!this.lostConnectionTone) return;

    this.lostConnectionTone.pause();
    this.lostConnectionTone.currentTime = 0;
  };

  @action
  playDisconnectedTone = () => {
    if (!this.localCallDisconnectedTone) return;

    this.localCallDisconnectedTone.currentTime = 0;
    this.localCallDisconnectedTone.volume = 0.5;
    void this.localCallDisconnectedTone.play().catch((error: Error) => {
      console.warn('Unable to play disconnected tone.', error);

      const errorName = error?.name || 'Unknown';
      const errorMessage = error?.message || 'No error message';

      const isExpectedError =
        errorName === 'NotAllowedError' || errorName === 'NotSupportedError';

      if (isExpectedError) {
        this.logger.error(
          `Disconnected tone error: ${errorName}; Message: ${errorMessage}; Timestamp: ${new Date().toISOString()}`
        );
      } else {
        bugsnagClient.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'disconnected';
        });
      }
    });
  };

  @action
  pauseDisconnectedTone = () => {
    if (!this.localCallDisconnectedTone) return;

    this.localCallDisconnectedTone.pause();
    this.localCallDisconnectedTone.currentTime = 0;
  };

  @action
  noInternetOnActiveCall = () => {
    this.clearConnectionTimers();
    this.setIsWebRTCReconnecting(true);
    this.reconnectionAttempts = 0; // Reset attempts

    // 5-second feedback timer
    this.setConnectionLossFeedbackTimer(
      setTimeout(() => {
        this.setIsConnectionLossFeedbackActive(true);
        this.playLostConnectionTone();
      }, CONNECTION_LOSS_FEEDBACK_DELAY)
    );

    // 13-second call drop timer
    this.setTimeOut = setTimeout(() => {
      if (this.isWebRTCReconnecting) {
        this.handleCallDrop();
      }
    }, CONNECTION_RETRY.TIMEOUT_MS);
  };

  @action
  handleCallDrop = () => {
    this.setIsCallTimeout(true);
    this.setIsWebRTCReconnecting(false);
    this.cancelCallOnNoWebRTC();
  };

  private reconnectionAttempts = 0;

  @action
  attemptReconnection = async () => {
    if (this.reconnectionAttempts < this.maxReconnectionAttempts) {
      this.reconnectionAttempts++;
      await this.reconnectSip();

      if (this.ua?.transport?.state === 'Connected') {
        this.setIsWebRTCConnected(true);
        this.connectionRestored();
      }

      // Reset the 13-second timer for another attempt
      if (this.setTimeOut) {
        clearTimeout(this.setTimeOut);
        this.setTimeOut = setTimeout(() => {
          if (this.isWebRTCReconnecting) {
            this.handleCallDrop();
          }
        }, CONNECTION_RETRY.TIMEOUT_MS);
      }
    } else {
      this.handleCallDrop();
    }
  };

  @action
  clearConnectionTimers = () => {
    if (this.connectionLossFeedbackTimer) {
      clearTimeout(this.connectionLossFeedbackTimer);
      this.setConnectionLossFeedbackTimer(null);
    }
    if (this.setTimeOut) {
      clearTimeout(this.setTimeOut);
      this.setTimeOut = null;
    }
    this.setIsConnectionLossFeedbackActive(false);
    this.pauseLostConnectionTone();
  };

  @action
  connectionRestored = () => {
    this.clearConnectionTimers();
    this.setIsWebRTCReconnecting(false);
    this.setIsCallTimeout(false);
    this.pauseLostConnectionTone();
  };

  // if isWebRTCReconnecting == clean timeout

  @action
  clearConnectionLossFeedback = () => {
    if (this.connectionLossFeedbackTimer) {
      clearTimeout(this.connectionLossFeedbackTimer);
      this.setConnectionLossFeedbackTimer(null);
    }
    this.setIsConnectionLossFeedbackActive(false);
    this.pauseLostConnectionTone();
  };

  @computed
  get AnyPhoneCallHasConferenceId() {
    return (
      this.phoneCalls.filter((p) => p.callUri?.includes(this.cRoom)).length > 0
    );
  }

  @computed
  get ConferenceIdGuid() {
    if (this.AnyPhoneCallHasConferenceId) {
      return this.phoneCalls.filter((p) => p.callUri?.includes(this.cRoom))[0]
        .conferenceId;
    } else {
      return null;
    }
  }

  /**
   * The list Of calls that are active and  on HOLD
   *
   * returns Empty Array if nothing is found
   */
  @computed
  get PhoneCallsOnHold() {
    return this.phoneCalls.filter((item) => {
      if (item.isCallConnected && item.isCallOnHold) {
        return item;
      }
    });
  }

  @computed
  get TransferMode() {
    return (
      this.phoneCalls.filter((item) => item.transferMode === true).length > 0
    );
  }

  /**
   * return the item that is in hold for transfer
   */
  @computed
  get callBeingTransfered() {
    return this.phoneCalls.find((item) => item.transferMode);
  }

  @action
  removeFromPhoneCallsArray = (s: Session) => {
    this.phoneCalls.forEach((item) => {
      if (item.session.id === s.id) {
        runInAction(() => this.phoneCalls.remove(item));
      }
    });
  };

  /**
   * The function will Loop through all the sessions in the array and put them on hold
   */
  @action
  setOtherSessionsOnHold = (activeSession: Session) => {
    this.phoneCalls.forEach((item) => {
      if (
        item.session.id !== activeSession.id &&
        item.session.state === 'Established'
      ) {
        void item.hold();
      }
    });
  };

  @action
  makeCallActive = (session: Session) => {
    const index = this.phoneCalls.findIndex(
      (item) => item.session.id === session.id
    );
    this.phoneCalls.push(this.phoneCalls.splice(index, 1)[0]);
  };

  /**
   * The list of calls connecting (Incoming or outgoing)
   */
  @computed
  get InactiveCalls() {
    return this.phoneCalls.filter((item) => item.isCallConnecting);
  }
}
